import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { api } from './lib/api';
import WhatsAppButton from './components/WhatsAppButton';
import AstemasterQueueWidget from './components/AstemasterQueueWidget';
import B2cPhases from './components/B2cPhases';
import { useIsMobile, MobileOnlyNotice } from './components/MobileOnly';

const MOBILE_MQ = '(max-width: 768px)';
const isMobile = () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches;

// ============================================================
// Icons — lucide-style, stroke based (ported from app.jsx)
// ============================================================
const Icon = ({ size = 18, stroke = 1.75, className = '', children }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {children}
  </svg>
);

const IconInbox = (p) => (
  <Icon {...p}>
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </Icon>
);
const IconUsers = (p) => (
  <Icon {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);
const IconBuilding = (p) => (
  <Icon {...p}>
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" /><path d="M16 6h.01" />
    <path d="M12 6h.01" /><path d="M12 10h.01" /><path d="M12 14h.01" />
    <path d="M16 10h.01" /><path d="M16 14h.01" />
    <path d="M8 10h.01" /><path d="M8 14h.01" />
  </Icon>
);
const IconHandshake = (p) => (
  <Icon {...p}>
    <path d="m11 17 2 2a1 1 0 1 0 3-3" />
    <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
    <path d="m21 3 1 11h-2" />
    <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
    <path d="M3 4h8" />
  </Icon>
);
const IconChevron = (p) => (<Icon {...p}><polyline points="15 18 9 12 15 6" /></Icon>);
const IconSearch = (p) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </Icon>
);
const IconBell = (p) => (
  <Icon {...p}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </Icon>
);
const IconSettings = (p) => (
  <Icon {...p}>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);
const IconHelp = (p) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </Icon>
);
const IconChevronDown = (p) => (<Icon {...p}><polyline points="6 9 12 15 18 9" /></Icon>);
const IconPanelLeft = (p) => (
  <Icon {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="9" y1="3" x2="9" y2="21" />
  </Icon>
);
const IconLogout = (p) => (
  <Icon {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </Icon>
);

// ============================================================
// Nav config
// ============================================================
const IconMap = (p) => (
  <Icon {...p}>
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
    <line x1="8" y1="2" x2="8" y2="18" />
    <line x1="16" y1="6" x2="16" y2="22" />
  </Icon>
);

// Icona Agent (terminale / robot ascii feel)
const IconAgent = (p) => (
  <Icon {...p}>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="6" y1="21" x2="18" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
    <polyline points="6 8 9 11 6 14" />
    <line x1="11" y1="14" x2="16" y2="14" />
  </Icon>
);

// Icona Dashboard (grid 2x2 stilizzata)
const IconDashboard = (p) => (
  <Icon {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </Icon>
);

// Icona Lock (lucchetto chiuso)
const IconLock = (p) => (
  <Icon {...p}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Icon>
);

const NAV = [
  { id: 'dashboard', to: '/app/dashboard', label: 'Workflow', Icon: IconDashboard },
  { id: 'lead', to: '/app/lead', label: 'Lead', Icon: IconUsers },
  { id: 'match', to: '/app/match', label: 'Match Inbox', Icon: IconInbox, badgeTone: 'accent' },
  { id: 'immobili', to: '/app/immobili', label: 'Immobili', Icon: IconBuilding },
  { id: 'mappa', to: '/app/mappa', label: 'Mappa', Icon: IconMap },
  { id: 'trattative', to: '/app/trattative', label: 'Trattative', Icon: IconHandshake },
  { id: 'agent', to: '/app/agent', label: 'Agent', Icon: IconAgent },
];

// ============================================================
// NavItem — anchor che renderizza nav__item con data-active.
// Se `locked=true`: renderizza come button, blocca la navigation e mostra il
// messaggio di sblocco (workflow da completare prima).
// ============================================================
function NavItem({ to, icon: ItemIcon, label, badge, badgeTone, collapsed, onNavigate, locked, lockedMessage, justUnlocked }) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  if (locked) {
    return (
      <button
        type="button"
        title={collapsed ? `${label} · bloccato` : undefined}
        className="nav__item"
        data-active="false"
        style={{
          textDecoration: 'none',
          opacity: 0.55,
          cursor: 'not-allowed',
          background: 'transparent',
          border: 0,
          width: '100%',
          textAlign: 'left',
        }}
        onClick={() => window.alert(lockedMessage || 'Completa il workflow per sbloccare questa sezione.')}
      >
        <ItemIcon size={17} />
        <span className="nav__label-text">{label}</span>
        <span className="nav__badge" data-tone="muted" style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 4px', background: 'transparent', border: 0,
        }}>
          <IconLock size={13} />
        </span>
      </button>
    );
  }

  // Animazione "just unlocked": glow per 2.5s + lock-icon che dissolve dentro al badge.
  // Triggera quando il prop justUnlocked passa da false → true.
  return (
    <Link
      to={to}
      title={collapsed ? label : undefined}
      className={`nav__item${justUnlocked ? ' nav__item--just-unlocked' : ''}`}
      data-active={isActive ? 'true' : 'false'}
      style={{
        textDecoration: 'none',
        ...(justUnlocked ? {
          animation: 'navUnlockGlow 1.8s ease-out',
          position: 'relative',
        } : null),
      }}
      onClick={onNavigate}
    >
      <ItemIcon size={17} />
      <span className="nav__label-text">{label}</span>
      {justUnlocked && (
        // Dissolve del lucchetto: parte visibile + ruota e svanisce in 1.2s
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--fg-muted)', animation: 'lockFadeOut 1.2s ease-in-out forwards',
        }}>
          <IconLock size={13} />
        </span>
      )}
      {!justUnlocked && badge != null && (
        <span className="nav__badge" data-tone={badgeTone || 'muted'}>
          {badge}
        </span>
      )}
    </Link>
  );
}

// ============================================================
// Sidebar
// ============================================================
function Sidebar({ collapsed, setCollapsed, onNavigate }) {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  const isB2c = !!user?.is_b2c;
  const isProspect = !!user?.is_prospect;
  // Per i clienti B2C nascondiamo tutte le voci pensate per il workflow B2B.
  // Per i prospect (lead invitati via magic-link) vediamo solo la Mappa.
  const navItems = isProspect
    ? NAV.filter((n) => n.id === 'mappa')
    : isB2c
    ? NAV.filter((n) => ['dashboard', 'immobili', 'mappa'].includes(n.id))
    : NAV;

  // Map: id voce sidebar → key dello step che la sblocca.
  // Sblocco progressivo: ogni step done sblocca certe sezioni.
  const UNLOCK_MAP = {
    lead: 'lead',
    match: 'match',
    immobili: 'match',
    mappa: 'match',
    trattative: 'proposta',
  };
  // Voci sempre sbloccate (escluse dal gating onboarding).
  const ALWAYS_UNLOCKED = new Set(['dashboard', 'agent']);

  // Stato onboarding completo (lista steps con done) + polling.
  const [stepsDoneMap, setStepsDoneMap] = useState({}); // { lead: true, match: false, ... }
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get('/api/dashboard/today');
        if (!alive) return;
        const map = Object.fromEntries((data?.onboarding?.steps || []).map((s) => [s.key, !!s.done]));
        setStepsDoneMap(map);
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Voci "appena sbloccate" → animazione momentanea. Set di ids in transizione false→true.
  const [justUnlockedIds, setJustUnlockedIds] = useState(new Set());
  // Salvo come STRING ordinata (es. "dashboard,lead") invece di Set: stable per la diff,
  // evita falsi cambi di reference su poll che ritornano stessi dati.
  const prevUnlockedKeyRef = useRef(null);

  useEffect(() => {
    // Aspetta che stepsDoneMap abbia DATI REALI dall'API prima di calcolare alcunché.
    // Sul primo render stepsDoneMap = {} (loading) → considerare quel momento come
    // baseline farebbe sembrare tutte le voci "appena sbloccate" quando i dati
    // arrivano poi dall'API. Quindi: salta finché map è vuoto.
    if (Object.keys(stepsDoneMap).length === 0) return;

    const unlockedIds = NAV
      .filter((n) => ALWAYS_UNLOCKED.has(n.id) || (stepsDoneMap[UNLOCK_MAP[n.id]] === true))
      .map((n) => n.id);
    const currentKey = [...unlockedIds].sort().join(',');

    // Primo passaggio con dati reali: setta la baseline senza animare.
    // Da qui in poi, qualunque transizione false→true scatena l'animazione.
    if (prevUnlockedKeyRef.current === null) {
      prevUnlockedKeyRef.current = currentKey;
      return;
    }

    // Se la chiave è IDENTICA → niente è cambiato, NIENTE animazione.
    if (prevUnlockedKeyRef.current === currentKey) {
      return;
    }

    const prevSet = new Set(prevUnlockedKeyRef.current.split(',').filter(Boolean));
    const newly = unlockedIds.filter((id) => !prevSet.has(id));
    prevUnlockedKeyRef.current = currentKey;

    if (newly.length > 0) {
      setCollapsed(false);
      setJustUnlockedIds(new Set(newly));
      // NOTA: il timer di reset NON è qui (sarebbe cancellato dal cleanup
      // a ogni poll del dataset) — sta in un effect separato che dipende
      // da justUnlockedIds, sotto.
    }
  }, [stepsDoneMap, setCollapsed]);

  // Reset auto dell'animazione 2.5s dopo il trigger. Isolato dall'effect
  // di detection così i poll periodici non cancellano il timer.
  useEffect(() => {
    if (justUnlockedIds.size === 0) return;
    const t = setTimeout(() => setJustUnlockedIds(new Set()), 2500);
    return () => clearTimeout(t);
  }, [justUnlockedIds]);

  // === Listener cross-component: Dashboard emette 'rivelasta:dashboard-changed'
  //     dopo ogni save lead → ri-fetchiamo subito senza aspettare il polling 10s.
  useEffect(() => {
    const handler = async () => {
      try {
        const { data } = await api.get('/api/dashboard/today');
        const map = Object.fromEntries((data?.onboarding?.steps || []).map((s) => [s.key, !!s.done]));
        setStepsDoneMap(map);
      } catch {}
    };
    window.addEventListener('rivelasta:dashboard-changed', handler);
    return () => window.removeEventListener('rivelasta:dashboard-changed', handler);
  }, []);

  const lockedMessage = 'Per sbloccare questa sezione completa prima il workflow nella Dashboard.';
  const isUnlocked = (navId) => ALWAYS_UNLOCKED.has(navId) || stepsDoneMap[UNLOCK_MAP[navId]] === true;

  return (
    <aside className="sidebar" data-collapsed={collapsed} aria-label="Navigazione principale">
      {/* Keyframes per l'animazione "appena sbloccato" — glow primario + dissolve del lucchetto */}
      <style>{`
        @keyframes navUnlockGlow {
          0%   { background: rgba(37, 99, 235, 0); box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
          15%  { background: rgba(37, 99, 235, 0.18); box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.15); }
          40%  { background: rgba(37, 99, 235, 0.10); box-shadow: 0 0 0 8px rgba(37, 99, 235, 0); }
          100% { background: rgba(37, 99, 235, 0); box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
        }
        @keyframes lockFadeOut {
          0%   { opacity: 1; transform: scale(1) rotate(0deg); }
          40%  { opacity: 1; transform: scale(1.15) rotate(-12deg); }
          100% { opacity: 0; transform: scale(0.6) rotate(-30deg); }
        }
      `}</style>
      <div className="sidebar__brand">
        <div className="brand__mark" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2 4 6v6c0 5 3.5 9.5 8 10 4.5-.5 8-5 8-10V6l-8-4z" />
          </svg>
        </div>
        <div className="brand__text">
          <div className="brand__name">Rivelasta</div>
          <div className="brand__sub">Studio Legale</div>
        </div>
        <button
          className="sidebar__collapse"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
          title={collapsed ? 'Espandi' : 'Comprimi'}
        >
          <IconChevron size={15} />
        </button>
      </div>

      <div className="sidebar__search">
        <div className="search">
          <IconSearch size={15} className="search__icon" />
          <input type="text" className="search__input" placeholder="Cerca…" aria-label="Cerca" />
          <kbd className="search__kbd">⌘K</kbd>
        </div>
        <button className="search__compact" aria-label="Cerca" title="Cerca (⌘K)">
          <IconSearch size={16} />
        </button>
      </div>

      <nav className="sidebar__nav" aria-label="Sezioni">
        <div className="nav__label">Workspace</div>
        <ul className="nav__list">
          {navItems.map((item) => {
            // Sblocco progressivo: dashboard sempre on; altri dipendono dallo step
            // della UNLOCK_MAP che li abilita. Per B2C tutto sbloccato.
            const locked = !isB2c && !isUnlocked(item.id);
            return (
              <li key={item.id}>
                <NavItem
                  to={item.to}
                  icon={item.Icon}
                  label={item.label}
                  badge={item.badge}
                  badgeTone={item.badgeTone}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                  locked={locked}
                  lockedMessage={lockedMessage}
                  justUnlocked={justUnlockedIds.has(item.id)}
                />
              </li>
            );
          })}
        </ul>

        {isSuperAdmin && (
          <>
            <div className="nav__label nav__label--spaced">Super Admin</div>
            <ul className="nav__list">
              <li>
                <NavItem
                  to="/app/admin/organizations"
                  icon={IconLock}
                  label="Organizzazioni"
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              </li>
              <li>
                <NavItem
                  to="/app/admin/aste-vinte"
                  icon={IconLock}
                  label="Aste vinte"
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              </li>
            </ul>
          </>
        )}

        <div className="nav__label nav__label--spaced">Generale</div>
        <ul className="nav__list">
          <li>
            <button className="nav__item">
              <IconBell size={17} />
              <span className="nav__label-text">Notifiche</span>
              <span className="nav__badge" data-tone="dot" aria-label="3 non lette"></span>
            </button>
          </li>
          <li>
            <NavItem
              to="/app/impostazioni"
              icon={IconSettings}
              label="Impostazioni"
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          </li>
          <li>
            <button className="nav__item">
              <IconHelp size={17} />
              <span className="nav__label-text">Supporto</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* Prospect: footer con info utente + logout (sostituiscono la topbar nascosta) */}
      {isProspect && <ProspectSidebarFooter onNavigate={onNavigate} />}
    </aside>
  );
}

// Drawer dedicato al prospect (BTC mobile redesign): sostituisce la <Sidebar>
// B2B con un drawer minimale stile design zip — avatar, badge Radar Pro,
// 2 stat box, menu con icon-square colorate, logout in fondo.
function ProspectDrawer({ collapsed, setCollapsed, onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const name = user?.name || 'Prospect';
  const initial = (name.charAt(0) || 'P').toUpperCase();
  const handleLogout = async () => {
    await logout();
    onNavigate?.();
    navigate('/login');
  };
  const close = () => setCollapsed(true);
  const goImpostazioni = () => { close(); navigate('/app/impostazioni'); };
  const menu = [
    { icon: '◎', label: 'I miei radar',      bg: '#E8EEF6', badge: '1', onClick: close },
    { icon: '📄', label: 'Report salvati',    bg: '#EAF3EE', badge: '',  onClick: close },
    { icon: '★', label: 'Immobili salvati',   bg: '#FBF1E2', badge: '',  onClick: close },
    { icon: '🔔', label: 'Notifiche AI',      bg: '#F4ECF6', badge: '',  onClick: close },
    { icon: '⚙', label: 'Impostazioni',      bg: '#F0EFEA', badge: '',  onClick: goImpostazioni },
  ];
  return (
    <aside className="pr-drawer" data-collapsed={collapsed} aria-label="Menu">
      <div className="pr-drawer__head">
        <div className="pr-drawer__avatar">{initial}</div>
        <div className="pr-drawer__userinfo">
          <div className="pr-drawer__name">{name}</div>
          <div className="pr-drawer__badge"><span>●</span> Radar Pro</div>
        </div>
      </div>

      <div className="pr-drawer__stats">
        <div className="pr-drawer__stat">
          <div className="pr-drawer__stat-val">1</div>
          <div className="pr-drawer__stat-lbl">radar attivi</div>
        </div>
        <div className="pr-drawer__stat">
          <div className="pr-drawer__stat-val">12k</div>
          <div className="pr-drawer__stat-lbl">immobili indicizzati</div>
        </div>
      </div>

      <nav className="pr-drawer__menu">
        {menu.map((it) => (
          <button key={it.label} className="pr-drawer__item" onClick={it.onClick || close}>
            <div className="pr-drawer__item-icon" style={{ background: it.bg }}>{it.icon}</div>
            <span className="pr-drawer__item-lbl">{it.label}</span>
            {it.badge && <span className="pr-drawer__item-badge">{it.badge}</span>}
          </button>
        ))}
      </nav>

      <div className="pr-drawer__spacer" />
      <button onClick={handleLogout} className="pr-drawer__logout">
        <div className="pr-drawer__item-icon" style={{ background: '#FBE9E7' }}>↩</div>
        <span>Esci</span>
      </button>
    </aside>
  );
}

function ProspectSidebarFooter({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    onNavigate?.();
    navigate('/login');
  };
  return (
    <div style={{
      borderTop: '1px solid var(--border)',
      padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 8,
      background: 'var(--bg)',
    }}>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {user?.email}
      </div>
      <button onClick={handleLogout} style={{
        padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8,
        background: 'white', cursor: 'pointer', fontSize: 13, textAlign: 'left',
      }}>
        Esci
      </button>
    </div>
  );
}

// ============================================================
// Topbar
// ============================================================
function Topbar({ onToggleSidebar }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isProspect = !!user?.is_prospect;
  const current = NAV.find((n) => location.pathname.startsWith(n.to));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((s) => s[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '??';

  return (
    <header className="topbar">
      <div className="topbar__left">
        <button
          className="iconbtn topbar__menu"
          onClick={onToggleSidebar}
          aria-label="Comprimi sidebar"
          title="Comprimi sidebar"
        >
          <IconPanelLeft size={17} />
        </button>
        <div className="crumbs">
          <span className="crumbs__item crumbs__item--muted">Workspace</span>
          <span className="crumbs__sep">/</span>
          <span className="crumbs__item">{current ? current.label : ''}</span>
        </div>
      </div>
      <div className="topbar__right">
        {!isProspect && <AstemasterQueueWidget />}
        {!isProspect && <WhatsAppButton />}
        {!isProspect && (
          <button className="iconbtn" aria-label="Notifiche">
            <IconBell size={17} />
            <span className="iconbtn__dot"></span>
          </button>
        )}
        <div className="topbar__divider"></div>
        <button className="topbar__user" aria-label="Profilo utente" title={user?.email}>
          <span className="user__avatar">{initials}</span>
          <span className="user__text">
            <span className="user__name">{user?.name || 'Utente'}</span>
            <span className="user__role">{user?.organization?.name || user?.email || ''}</span>
          </span>
          <IconChevronDown size={14} className="user__chev" />
        </button>
        <button
          className="iconbtn"
          aria-label="Esci"
          title="Esci"
          onClick={handleLogout}
        >
          <IconLogout size={17} />
        </button>
      </div>
    </header>
  );
}

// ============================================================
// Shell
// ============================================================
// Floating hamburger overlay per gli utenti B2C: niente topbar che occupa
// spazio, solo un'icona "tre lineette" in alto a sinistra che apre il menu.
function B2cFloatingMenu({ onToggle }) {
  return (
    <button
      type="button"
      className="b2c-floating-menu"
      onClick={onToggle}
      aria-label="Apri menu"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.2"
           strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>
  );
}

export default function AppShell() {
  const { user } = useAuth();
  const isB2c = !!user?.is_b2c;
  const isProspect = !!user?.is_prospect;
  const mobile = useIsMobile();
  // collapsed=true on mobile means drawer CLOSED; on desktop means narrow rail
  const [collapsed, setCollapsed] = useState(() => isMobile());
  const location = useLocation();
  const navigate = useNavigate();

  // Prospect: consenti solo Home, mappa, impostazioni e dettaglio immobile;
  // qualsiasi altro path li riporta alla Home. Marina 2026-07-12.
  useEffect(() => {
    if (!isProspect) return;
    const p = location.pathname;
    const allowed = p === '/app/home' || p === '/app/mappa' || p === '/app/analizzati' || p === '/app/profilo' || p === '/app/impostazioni' || /^\/app\/immobili\/\d+/.test(p);
    if (!allowed) navigate('/app/home', { replace: true });
  }, [isProspect, location.pathname, navigate]);

  // Utente di ALTRO ruolo (non prospect) che apre una pagina B2C-only → lo mandiamo
  // su /b2c, che effettua il logout automatico e mostra il form del numero WhatsApp.
  // Marina 2026-07-14.
  useEffect(() => {
    if (!user || isProspect) return;
    const p = location.pathname;
    const prospectOnly = p === '/app/home' || p === '/app/analizzati' || p === '/app/profilo';
    if (prospectOnly) navigate('/b2c', { replace: true });
  }, [user, isProspect, location.pathname, navigate]);

  // Single-session heartbeat: ogni 5 min verifica che questa sessione non sia
  // stata superata da un nuovo login con lo stesso account; in tal caso esce.
  // Marina 2026-07-12.
  useEffect(() => {
    if (!user || !isProspect) return; // solo login WhatsApp (prospect)
    let stop = false;
    const check = () => api.get('/api/auth/heartbeat').catch((e) => {
      if (!stop && e?.response?.status === 401) {
        window.location.href = '/b2c?superseded=1'; // sessione presa da un altro accesso
      }
    });
    const iv = setInterval(check, 5 * 60 * 1000);
    return () => { stop = true; clearInterval(iv); };
  }, [user?.id, isProspect]);

  // Close drawer on route change (mobile)
  useEffect(() => {
    if (isMobile()) setCollapsed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Reset sensible default when crossing the breakpoint
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_MQ);
    const onChange = (e) => setCollapsed(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const closeDrawer = () => {
    if (isMobile()) setCollapsed(true);
  };

  // La parte B2C (prospect) è disponibile SOLO da mobile. Il B2B resta su PC.
  if (isProspect && !mobile) return <MobileOnlyNotice />;

  return (
    <div className="app">
      {isProspect
        ? <ProspectDrawer collapsed={collapsed} setCollapsed={setCollapsed} onNavigate={closeDrawer} />
        : <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} onNavigate={closeDrawer} />}
      {/* Backdrop: only visible on mobile when drawer is open */}
      {!collapsed && (
        <button
          type="button"
          aria-label="Chiudi menu"
          onClick={closeDrawer}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'transparent',
            border: 0,
            zIndex: 99,
            display: 'none',
          }}
          className="app__backdrop"
        />
      )}
      <div className={`main${isB2c ? ' main--b2c' : ''}`}>
        {isProspect ? null : isB2c ? (
          <B2cFloatingMenu onToggle={() => setCollapsed((c) => !c)} />
        ) : (
          <Topbar onToggleSidebar={() => setCollapsed((c) => !c)} />
        )}
        {!isB2c && !isProspect && <B2cPhases />}
        <Outlet context={{ toggleSidebar: () => setCollapsed((c) => !c) }} />
      </div>
    </div>
  );
}
